import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Loading from "../common/Loading";
import { ACCENT, PrimaryButton } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { getMusicPreferences, saveMusicPreferences } from "../../api/notificationInbox";

// Artist Studio > Notifications > Preferences (FSD 6.9.1, FR-NOT-002): one row per kind of notice with In-app, Email and Push
// switches. Legal notices (DMCA notice, claim, strike, appeal decision) are locked on with a lock icon and the tooltip
// "Required by law". A channel a notice never uses is shown switched off and disabled. Licence sales and tips go by email as one
// daily summary.

const CHANNELS = [
  { id: "in_app", label: "In-app" },
  { id: "email", label: "Email" },
  { id: "push", label: "Push" },
];

const messageOf = (e, fallback) => (e && e.message) || fallback;

function Switch({ checked, disabled, onChange, label, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed ${disabled ? "opacity-60" : ""}`}
      style={{ backgroundColor: checked ? ACCENT : "#d1d5db", "--tw-ring-color": ACCENT }}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

function PreferencesBody() {
  const [categories, setCategories] = useState(null);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setError("");
    setCategories(null);
    getMusicPreferences().then(
      (r) => { setCategories(r.categories || []); setDraft({}); },
      (e) => { setError(messageOf(e, "We could not load your notification settings. Please try again.")); setCategories([]); }
    );
  }, []);

  useEffect(() => { load(); }, [load]);

  const valueOf = (cat, channel) => (draft[cat.key] && typeof draft[cat.key][channel] === "boolean" ? draft[cat.key][channel] : cat[channel].value);
  const toggle = (cat, channel) => setDraft((d) => ({ ...d, [cat.key]: { ...(d[cat.key] || {}), [channel]: !valueOf(cat, channel) } }));
  const changed = Object.keys(draft).length > 0;

  const save = async () => {
    if (saving || !changed) return;
    setSaving(true);
    setError("");
    try {
      const preferences = Object.entries(draft).map(([category, values]) => ({ category, ...values }));
      const r = await saveMusicPreferences(preferences);
      setCategories(r.categories || []);
      setDraft({});
      toast.success("Notification settings saved.");
    } catch (e) {
      setError(messageOf(e, "We could not save your settings. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="notification-preferences">
      <Link to="/Notification" className="text-sm font-semibold text-gray-700 hover:underline">← Back to Notifications</Link>
      <h1 className="mt-4 text-[32px] font-bold leading-tight text-gray-900">Notification settings</h1>
      <p className="mt-2 text-gray-600">Choose how you hear about your account, tracks and money. Legal notices are always sent.</p>

      {error && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="preferences-error">
          {error} {categories && categories.length === 0 && <button type="button" className="underline font-semibold" onClick={load}>Try again</button>}
        </p>
      )}

      <div className="mt-5 overflow-x-auto rounded-2xl bg-[#f1f6f2]">
        {categories === null ? (
          <div className="flex justify-center py-16"><Loading /></div>
        ) : (
          <table className="w-full text-left text-[15px]">
            <thead className="text-xs font-bold uppercase tracking-wide" style={{ color: ACCENT }}>
              <tr>
                <th className="px-6 py-4">Notice</th>
                {CHANNELS.map((c) => <th key={c.id} className="px-4 py-4 text-center">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.key} className="border-t border-white/70" data-testid={`pref-${cat.key}`}>
                  <td className="px-6 py-4 text-gray-900">
                    <span className="font-semibold">{cat.label}</span>
                    {cat.legal && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-gray-600" title={cat.tooltip || "Required by law"} data-testid="legal-lock">
                        <span aria-hidden="true">🔒</span> {cat.tooltip || "Required by law"}
                      </span>
                    )}
                  </td>
                  {CHANNELS.map((c) => {
                    const info = cat[c.id];
                    const unavailable = !info.available;
                    const hint = info.locked ? cat.tooltip || "Required by law" : unavailable ? "Not sent on this channel" : info.digest ? "Sent once a day as a summary" : "";
                    return (
                      <td key={c.id} className="px-4 py-4 text-center" title={hint}>
                        <Switch
                          checked={unavailable ? false : info.locked ? true : valueOf(cat, c.id)}
                          disabled={info.locked || unavailable}
                          onChange={() => toggle(cat, c.id)}
                          label={`${c.label} for ${cat.label}`}
                          testId={`pref-${cat.key}-${c.id}`}
                        />
                        {info.digest && <p className="mt-1 text-[11px] text-gray-500">Daily summary</p>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {categories && categories.length > 0 && (
        <div className="mt-5 flex items-center gap-3">
          <PrimaryButton onClick={save} disabled={!changed || saving} data-testid="preferences-save">{saving ? "Saving…" : "Save changes"}</PrimaryButton>
          {changed && !saving && <span className="text-sm text-gray-500">You have unsaved changes.</span>}
        </div>
      )}
    </div>
  );
}

export default function NotificationPreferences() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="notifications" heading="" tabs={[]} maxWidth="max-w-4xl">
      <PreferencesBody />
    </StudioGate>
  );
}
